import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { later, cancel } from '@ember/runloop';
import { service } from '@ember/service';
import { isFeatureHintValid } from 'mewe/utils/feature-hint';
import ReputationApi from 'mewe/api/reputation-api';
import AccountApi from 'mewe/api/account-api';
export default class ProfileMainReputation extends Component {
  @service dynamicDialogs;
  @service analytics;

  @tracked isVotingEnabled = false;
  @tracked votedPoints = 0;
  @tracked disableUpHeartBtn = false;
  @tracked disableDownHeartBtn = false;
  @tracked isVoteSubmitted = false;
  @tracked votingStatus = this.args.votingStatus;
  @tracked isUpVotePressed = false;
  @tracked isDownVotePressed = false;

  initialVotedPoints = 0;
  pressTimer = null;
  pressInterval = null;
  INITIAL_DELAY = 100; // Wait 100ms before starting continuous increment
  INTERVAL_DELAY = 20; // Increment every 20ms after initial delay

  get canVote() {
    return this.isVoteSubmitted ? false : this.votingStatus.canVoteFor;
  }

  get isSubmitVoteDisabled() {
    return this.votedPoints === this.initialVotedPoints;
  }

  get voteTime() {
    if (this.isVoteSubmitted) {
      return new Date().getTime();
    }
    return this.votingStatus.lastTimeVotedFor;
  }

  get isUpHeart() {
    return this.votedPoints >= 0;
  }

  get heartSizeStyle() {
    const voteSize = Math.abs(this.votedPoints);
    return voteSize > 0 ?
      `transform: scale(${1 + (voteSize/100)}); top: ${(-106 - (voteSize/5))}px;` :
      '';
  }

  get votedPointsStyle() {
    const voteSize = Math.abs(this.votedPoints);
    return voteSize > 0 ?
      `top: ${(-89 - (voteSize * 0.5))}px;` :
      '';
  }

  willDestroy() {
    super.willDestroy();
    this.stopVoting();
  }

  reset() {
    this.isVoteSubmitted = false;
    this.isVotingEnabled = false;
    this.votedPoints = 0;
    this.disableUpHeartBtn = false;
    this.disableDownHeartBtn = false;
  }

  @action
  updateVotingStatus() {
    this.votingStatus = this.args.votingStatus;
    this.reset();
  }

  @action
  async resetVoting() {
    this.votingStatus = await ReputationApi.getVotingStatus(this.args.userId);
    this.reset();
  }

  @action
  startUpVote(event) {
    event.preventDefault();
    this.disableDownHeartBtn = false;
    if (!this.disableUpHeartBtn) {
      this.incrementVote();
      if (!this.disableUpHeartBtn) {
        this.pressTimer = later(() => {
          this.isUpVotePressed = true;
          this.pressInterval = setInterval(() => {
            this.incrementVote();
            if (this.disableUpHeartBtn) {
              this.stopVoting();
            }
          }, this.INTERVAL_DELAY);
        }, this.INITIAL_DELAY);
      }
    } else {
      this.dynamicDialogs.openDialog('reputation-info-dialog', {
        title: __('Vote limit reached'),
        description: __('You have up to 10 votes for each user (negative or positive)'),
      });
    }
  }

  incrementVote() {
    this.votedPoints = this.votedPoints + 1;
    this.disableUpHeartBtn = this.votedPoints === 100;
  }

  @action
  startDownVote(event) {
    event.preventDefault();
    this.disableUpHeartBtn = false;
    if (!this.disableDownHeartBtn) {
      this.decrementVote();
      if (!this.disableDownHeartBtn) {
        this.pressTimer = later(() => {
          this.isDownVotePressed = true;
          this.pressInterval = setInterval(() => {
            this.decrementVote();
            if (this.disableDownHeartBtn) {
              this.stopVoting();
            }
          }, this.INTERVAL_DELAY);
        }, this.INITIAL_DELAY);
      }
    } else {
      this.dynamicDialogs.openDialog('reputation-info-dialog', {
        title: __('Vote limit reached'),
        description: __('You have up to 10 votes for each user (negative or positive)'),
      });
    }
  }

  decrementVote() {
    this.votedPoints = this.votedPoints - 1;
    this.disableDownHeartBtn = this.votedPoints === -100;
  }

  @action
  stopVoting() {
    cancel(this.pressTimer);
    clearInterval(this.pressInterval);
    this.isUpVotePressed = false;
    this.isDownVotePressed = false;
  }

  @action
  showVoting() {
    this.isVotingEnabled = true;
    isFeatureHintValid('reputation-first-time-vote').then(({ timesViewed }) => {
      // the intent is to show the dialog only twice
      if (timesViewed <= 1) {
        this.dynamicDialogs.openDialog('crowdscore-dialog', {
          callback: () => {
            AccountApi.checkHint({ hint: 'reputation-first-time-vote' });
            this.analytics.sendEvent('popupViewed', 'Reputation Crowdscore');
          },
        });
      }
    });
  }

  @action
  closeVoting() {
    this.isVotingEnabled = false;
    this.disableUpHeartBtn = false;
    this.disableDownHeartBtn = false;
    this.votedPoints = 0;
  }

  @action
  async submitVote() {
    await ReputationApi.voteReputation({
      userId: this.args.userId,
      points: this.votedPoints,
    });
    this.isVoteSubmitted = true;
    const username = this.args.profileName.substring(0, 25) + (this.args.profileName.length > 25 ? '...' : '');
    if (this.votedPoints >= 0) {
      isFeatureHintValid('reputation-first-time-vote-positive').then(({ timesViewed }) => {
        if (timesViewed === 0) {
          this.dynamicDialogs.openDialog('reputation-info-dialog', {
            title: __('Vote submitted'),
            description: __('You just boosted {username}’s profile', {username: username}),
            callback: () => {
              AccountApi.checkHint({ hint: 'reputation-first-time-vote-positive' });
            },
          });
        }
      });
    } else {
      isFeatureHintValid('reputation-first-time-vote-negative').then(({ timesViewed }) => {
        if (timesViewed === 0) {
          this.dynamicDialogs.openDialog('reputation-info-dialog', {
            title: __('It is a bit sad!'),
            description: __('You just down voted {username}’s profile, this user will now have less visibility for you.', {username: username}),
            callback: () => {
              AccountApi.checkHint({ hint: 'reputation-first-time-vote-negative' });
            },
          });
        }
      });
    }
  }
}
